import { BrandModel as BrandApi } from '@/http/models/brand/Brand'
import { toast } from 'sonner'

import { useState } from 'react'

import { BrandModel } from '@/types'

import { useFetchBrands } from './useFetchBrands'

type DialogMode = 'create' | 'edit' | 'view'

export const useBrandsTable = () => {
  const [mode, setMode] = useState<DialogMode>('create')
  const [brandDialogOpen, setBrandDialogOpen] = useState(false)
  const [editBrand, setEditBrand] = useState<BrandModel.Brand | null>(null)

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [brandToDelete, setBrandToDelete] = useState<BrandModel.Brand | null>(null)

  const [createLoading, setCreateLoading] = useState(false)
  const [updateLoading, setUpdateLoading] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)

  const { loading, data, pagination, formValues, formMethods, setPage, setPageSize, refresh } =
    useFetchBrands({
      sort: null,
    })

  const openCreateDialog = () => {
    setMode('create')
    setEditBrand(null)
    setBrandDialogOpen(true)
  }

  const openViewDialog = (brand: BrandModel.Brand) => {
    setMode('view')
    setEditBrand(brand)
    setBrandDialogOpen(true)
  }

  const openEditDialog = (brand: BrandModel.Brand) => {
    setMode('edit')
    setEditBrand(brand)
    setBrandDialogOpen(true)
  }

  const closeDialog = () => {
    setBrandDialogOpen(false)
    setEditBrand(null)
  }

  const handleDeleteClick = (brand: BrandModel.Brand) => {
    setBrandToDelete(brand)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!brandToDelete?._id) return

    setDeleteLoading(true)
    const response = await BrandApi.delete({
      params: { id: brandToDelete._id },
    })

    if (response.success) {
      toast.success('Brand deleted successfully')
      setDeleteDialogOpen(false)
      setBrandToDelete(null)
      refresh()
    } else {
      toast.error((response.error?.data as any)?.message || 'Failed to delete brand')
    }
    setDeleteLoading(false)
  }

  const toggleStatus = async (brand: BrandModel.Brand) => {
    const newStatus = brand.status === 'active' ? 'inactive' : 'active'

    const response = await BrandApi.update({
      params: { id: brand._id },
      body: { status: newStatus },
    })

    if (response.success) {
      toast.success('Brand status updated')
      refresh()
    } else {
      toast.error('Failed to update status')
    }
  }

  const handleCreateBrand = async (data: any) => {
    setCreateLoading(true)
    const response = await BrandApi.create({
      body: data,
    })

    if (response.success) {
      toast.success('Brand created successfully')
      closeDialog()
      refresh()
    } else {
      toast.error((response.error?.data as any)?.message || 'Failed to create brand')
    }
    setCreateLoading(false)
  }

  const handleUpdateBrand = async (id: string, data: any) => {
    setUpdateLoading(true)
    const response = await BrandApi.update({
      params: { id },
      body: data,
    })

    if (response.success) {
      toast.success('Brand updated successfully')
      closeDialog()
      refresh()
    } else {
      toast.error((response.error?.data as any)?.message || 'Failed to update brand')
    }
    setUpdateLoading(false)
  }

  const stats = {
    totalBrands: pagination.totalCount,
    activeBrands: data.filter((brand) => brand.status === 'active').length,
    inactiveBrands: data.filter((brand) => brand.status === 'inactive').length,
  }

  return {
    loading,
    data,
    pagination,
    formValues,
    formMethods,
    stats,
    mode,
    brandDialogOpen,
    editBrand,
    deleteDialogOpen,
    brandToDelete,
    createLoading,
    updateLoading,
    deleteLoading,
    setPage,
    setPageSize,
    refresh,
    openCreateDialog,
    openViewDialog,
    openEditDialog,
    closeDialog,
    handleDeleteClick,
    handleDeleteConfirm,
    setDeleteDialogOpen,
    handleCreateBrand,
    handleUpdateBrand,
    toggleStatus,
  }
}
